import { makeStyles } from "@fluentui/react-components";
import { useState, type FC } from "react";
import { brandColors, fonts, inkColors, motion, statusColors, surfaceColors } from "../variant-c/tokens";

// ---------------------------------------------------------------------------
// Payload shape — mirrors WriteGate.build_prompt's JSON block
// ---------------------------------------------------------------------------

export interface WriteApprovalPayload {
  section_number: string;
  mode: "overwrite" | "insert";
  before: string;
  before_truncated: boolean;
  after: string;
  after_truncated: boolean;
  reason: string;
  granular_diff?: GranularDiffPayload;
}

export interface GranularDiffPayload {
  kind: "replace_text";
  match_id?: string;
  occurrence?: "first" | "all" | number;
  find_text: string;
  replace_text: string;
  before_excerpt: string;
  after_excerpt: string;
  before_range?: [number, number];
  after_range?: [number, number];
  context_chars?: number;
  full_preview_available?: boolean;
}

// ---------------------------------------------------------------------------
// Parsers — exported so callers can pre-parse before the component mounts
// ---------------------------------------------------------------------------

/** Extract the JSON block appended by WriteGate.build_prompt. */
export function parseWriteApprovalPayload(question: string): WriteApprovalPayload | null {
  const match = /\{[\s\S]*\}/u.exec(question);
  if (!match) return null;
  try {
    const obj = JSON.parse(match[0]);
    if (
      obj &&
      typeof obj === "object" &&
      typeof obj.section_number === "string" &&
      (obj.mode === "overwrite" || obj.mode === "insert")
    ) {
      return obj as WriteApprovalPayload;
    }
    return null;
  } catch {
    return null;
  }
}

/** Strip the trailing JSON block from a question string. */
export function stripJsonTail(question: string): string {
  return question.replace(/\{[\s\S]*\}/u, "").trim();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    fontFamily: fonts.body,
  },
  pane: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "10px",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    background: `linear-gradient(180deg, ${surfaceColors.paper} 0%, #FFFFFF 100%)`,
  },
  label: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: inkColors.ink3,
    fontFamily: fonts.mono,
  },
  beforeBox: {
    padding: "10px 12px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    fontSize: "12px",
    fontFamily: fonts.mono,
    lineHeight: "1.5",
    whiteSpace: "pre-wrap",
    overflowY: "auto",
    maxHeight: "220px",
    color: inkColors.ink2,
    boxSizing: "border-box",
  },
  emptyBefore: {
    fontStyle: "italic",
    color: inkColors.ink4,
  },
  afterTextarea: {
    width: "100%",
    minHeight: "132px",
    padding: "10px 12px",
    fontFamily: fonts.mono,
    fontSize: "12px",
    lineHeight: "1.5",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    background: "#FFFFFF",
    color: inkColors.ink,
    resize: "vertical",
    boxSizing: "border-box",
    transition: `border-color ${motion.transitionMs}ms ease, box-shadow ${motion.transitionMs}ms ease`,
    ":hover": {
      border: `1px solid ${brandColors.mid}`,
    },
    ":focus": {
      outline: "none",
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 0 0 2px ${brandColors.glow}`,
    },
  },
  truncationNote: {
    fontSize: "11px",
    fontStyle: "italic",
    color: inkColors.ink3,
  },
  granularGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
    },
  },
  granularBox: {
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "12px",
    lineHeight: "1.5",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    minHeight: "84px",
    boxSizing: "border-box",
    transition: `transform ${motion.transitionMs}ms ease, box-shadow ${motion.transitionMs}ms ease`,
    ":hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 10px rgba(15, 10, 30, 0.08)",
    },
  },
  granularBefore: {
    background: "#F7F3FF",
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink2,
  },
  granularAfter: {
    background: "#ECFDF5",
    border: `1px solid ${statusColors.cool}`,
    color: inkColors.ink,
  },
  granularMeta: {
    fontSize: "11px",
    color: inkColors.ink3,
    lineHeight: "1.5",
  },
  revealButton: {
    alignSelf: "flex-start",
    padding: "7px 12px",
    fontSize: "11px",
    borderRadius: "999px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#FFFFFF",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontWeight: 600,
    transition: `background-color ${motion.transitionMs}ms ease, border-color ${motion.transitionMs}ms ease, transform ${motion.transitionMs}ms ease`,
    ":hover": {
      background: surfaceColors.paperDim,
      border: `1px solid ${brandColors.mid}`,
      transform: "translateY(-1px)",
    },
    ":focus": {
      outline: "none",
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 0 0 2px ${brandColors.glow}`,
    },
    ":active": {
      transform: "translateY(0)",
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface WriteApprovalViewProps {
  payload: WriteApprovalPayload;
  editedAfter: string;
  onEditAfter: (value: string) => void;
}

const WriteApprovalView: FC<WriteApprovalViewProps> = ({ payload, editedAfter, onEditAfter }) => {
  const styles = useStyles();
  const [showFullContent, setShowFullContent] = useState(false);
  const hasGranularDiff = payload.mode === "overwrite" && payload.granular_diff?.kind === "replace_text";

  return (
    <div className={styles.root}>
      {hasGranularDiff && payload.granular_diff && (
        <div className={styles.pane}>
          <div className={styles.label}>Granular change</div>
          <div className={styles.granularMeta}>
            Replace "{payload.granular_diff.find_text}" with "{payload.granular_diff.replace_text}"
          </div>
          <div className={styles.granularGrid}>
            <div>
              <div className={styles.label}>Before</div>
              <div className={`${styles.granularBox} ${styles.granularBefore}`}>
                {payload.granular_diff.before_excerpt}
              </div>
            </div>
            <div>
              <div className={styles.label}>After</div>
              <div className={`${styles.granularBox} ${styles.granularAfter}`}>
                {payload.granular_diff.after_excerpt}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.revealButton}
            onClick={() => setShowFullContent((current) => !current)}
          >
            {showFullContent ? "Hide full content" : "Show full content"}
          </button>
        </div>
      )}

      {payload.mode === "overwrite" && (!hasGranularDiff || showFullContent) && (
        <div className={styles.pane}>
          <div className={styles.label}>Current content (before)</div>
          {payload.before ? (
            <div className={styles.beforeBox}>{payload.before}</div>
          ) : (
            <div className={`${styles.beforeBox} ${styles.emptyBefore}`}>(empty)</div>
          )}
          {payload.before_truncated && (
            <div className={styles.truncationNote}>Preview truncated — full content available in the document.</div>
          )}
        </div>
      )}

      {(!hasGranularDiff || showFullContent || payload.mode !== "overwrite") && (
        <div className={styles.pane}>
          <div className={styles.label}>
            {payload.mode === "overwrite" ? "Proposed replacement (after)" : "Proposed content"}
            {" — edit if needed"}
          </div>
          <textarea
            className={styles.afterTextarea}
            value={editedAfter}
            onChange={(e) => onEditAfter(e.target.value)}
            spellCheck={false}
            aria-label="Edit replacement content before approving"
          />
          {payload.after_truncated && (
            <div className={styles.truncationNote}>
              After-content was truncated in this preview. The agent holds the full text; approving without edits will
              apply the full version.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WriteApprovalView;
